import { useCallback, useEffect, useRef, useState } from "react"

// Custom hook for handling horizontal (side) drag/touch gestures
const useDragSidescroll = (
  scrollerRef: React.RefObject<HTMLDivElement>,
  hasOverflow: boolean,
  scrollBehavior?: string
) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isRecentlyDragged, setIsRecentlyDragged] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [hasDragStarted, setHasDragStarted] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  // Store the bounding rect for the current drag
  const dragBoundaryRef = useRef<DOMRect | null>(null)

  // Minimum distance in pixels before considering it a drag
  const DRAG_THRESHOLD = 5

  const handleStart = useCallback(
    (clientX: number) => {
      if (!scrollerRef.current || !hasOverflow) return

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      // Save the bounding rect for this drag
      dragBoundaryRef.current = scrollerRef.current.getBoundingClientRect()

      // Set up potential drag, but don't start dragging yet
      setStartX(clientX)
      setScrollLeft(scrollerRef.current.scrollLeft)
      setHasDragStarted(false)
      setIsRecentlyDragged(false)

      // Don't set isDragging yet - wait for movement threshold
    },
    [scrollerRef, hasOverflow]
  )

  // Helper to check if pointer is inside the drag boundary
  const isPointerInBoundary = useCallback((clientX: number, clientY: number) => {
    const rect = dragBoundaryRef.current
    if (!rect) return false
    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    )
  }, [])

  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!scrollerRef.current || !hasOverflow) return

      // If pointer leaves the boundary, reset drag state
      if (isDragging && !isPointerInBoundary(clientX, clientY)) {
        resetDragState()
        return
      }

      const deltaX = Math.abs(clientX - startX)

      // Only start dragging if we've moved beyond the threshold
      if (!hasDragStarted && deltaX > DRAG_THRESHOLD) {
        setIsDragging(true)
        setHasDragStarted(true)

        // Add cursor style to indicate dragging
        scrollerRef.current.style.cursor = "grabbing"
        scrollerRef.current.style.userSelect = "none"
        // Temporarily disable scroll snap during drag for smooth movement
        if (scrollBehavior === "snap") {
          scrollerRef.current.style.scrollSnapType = "none"
        }
      }

      // Only actually scroll if we're in drag mode
      if (hasDragStarted && isDragging) {
        const scrollDelta = clientX - startX
        const newScrollLeft = scrollLeft - scrollDelta

        // Smooth scrolling during drag
        scrollerRef.current.scrollLeft = newScrollLeft
      }
    },
    [startX, scrollLeft, scrollerRef, scrollBehavior, hasDragStarted, isDragging, hasOverflow, isPointerInBoundary]
  )

  const handleEnd = useCallback(() => {
    if (!scrollerRef.current) return

    // Only treat it as a drag if we actually started dragging
    if (hasDragStarted && isDragging) {
      setIsDragging(false)
      setIsRecentlyDragged(true)
      scrollerRef.current.style.cursor = "grab"
      scrollerRef.current.style.userSelect = "auto"

      // Re-enable scroll snap after drag ends
      if (scrollBehavior === "snap") {
        scrollerRef.current.style.scrollSnapType = "x mandatory"
        // Trigger snap behavior by slightly adjusting scroll position
        requestAnimationFrame(() => {
          if (scrollerRef.current) {
            const currentScroll = scrollerRef.current.scrollLeft
            scrollerRef.current.scrollLeft = currentScroll + 0.1
          }
        })
      }

      // Set timeout to prevent clicks for 300ms after drag ends
      timeoutRef.current = setTimeout(() => {
        setIsRecentlyDragged(false)
        timeoutRef.current = null
      }, 300)
    } else {
      // It was just a click, reset everything immediately
      setIsDragging(false)
      setIsRecentlyDragged(false)
      setHasDragStarted(false)
    }
    // Clear drag boundary
    dragBoundaryRef.current = null
  }, [scrollerRef, scrollBehavior, hasDragStarted, isDragging])

  // Mouse events
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      handleStart(e.clientX)
    },
    [handleStart]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY)
    },
    [handleMove]
  )

  const handleMouseUp = useCallback(() => {
    handleEnd()
  }, [handleEnd])

  // Touch events
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      handleStart(e.touches[0].clientX)
    },
    [handleStart]
  )

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      // Only prevent default if we're actually dragging
      if (hasDragStarted && isDragging) {
        e.preventDefault() // Prevent scrolling the page
      }
      handleMove(e.touches[0].clientX, e.touches[0].clientY)
    },
    [handleMove, hasDragStarted, isDragging]
  )

  const handleTouchEnd = useCallback(() => {
    handleEnd()
  }, [handleEnd])

  // Reset drag state function
  const resetDragState = useCallback(() => {
    if (!scrollerRef.current) return

    // Clear any active drag state
    setIsDragging(false)
    setIsRecentlyDragged(false)
    setHasDragStarted(false)
    setStartX(0)
    setScrollLeft(0)

    // Clear timeout if it exists
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    // Reset cursor and user select
    scrollerRef.current.style.cursor = hasOverflow ? "grab" : "default"
    scrollerRef.current.style.userSelect = "auto"

    // Re-enable scroll snap if it was disabled
    if (scrollBehavior === "snap") {
      scrollerRef.current.style.scrollSnapType = "x mandatory"
    }
    // Clear drag boundary
    dragBoundaryRef.current = null
  }, [scrollerRef, hasOverflow, scrollBehavior])

  // Update drag boundary on window resize if dragging
  useEffect(() => {
    if (!isDragging) return
    const updateBoundary = () => {
      if (scrollerRef.current) {
        dragBoundaryRef.current = scrollerRef.current.getBoundingClientRect()
      }
    }
    window.addEventListener("resize", updateBoundary)
    return () => {
      window.removeEventListener("resize", updateBoundary)
    }
  }, [isDragging, scrollerRef])

  // Set up event listeners
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return

    // Set cursor style based on whether dragging is available
    scroller.style.cursor = hasOverflow ? "grab" : "default"

    // Only set up drag event listeners if there's overflow
    if (hasOverflow && startX !== 0) {
      // Add global event listeners when we might be dragging
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false
      })
      document.addEventListener("touchend", handleTouchEnd)
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      document.removeEventListener("touchmove", handleTouchMove)
      document.removeEventListener("touchend", handleTouchEnd)
    }
  }, [
    startX,
    hasOverflow,
    handleMouseMove,
    handleMouseUp,
    handleTouchMove,
    handleTouchEnd
  ])

  // Set up window blur listener to reset drag state
  useEffect(() => {
    const handleWindowBlur = () => {
      if (isDragging || isRecentlyDragged || hasDragStarted) {
        resetDragState()
      }
    }

    window.addEventListener("blur", handleWindowBlur)

    return () => {
      window.removeEventListener("blur", handleWindowBlur)
    }
  }, [isDragging, isRecentlyDragged, hasDragStarted, resetDragState])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return {
    handleMouseDown,
    handleTouchStart,
    isDragging,
    isRecentlyDragged,
    resetDragState
  }
}

export default useDragSidescroll
